import { authServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';

export type PermissionDefinition = {
  code: string;
  name: string;
  description: string;
  category: string;
  portal: string;
};

export type PermissionDefinitionFilter = {
  category?: string;
  search?: string;
};

// The Auth service is not behind the portal's /api proxy, so this addresses it directly the way
// notificationPreferencesRepository addresses Communications. The route is already scoped to the
// company portal, so it never returns tenant, owner, vendor or MagicDoor-internal permissions.
const PERMISSIONS_URL = `${authServiceUrl()}/company-portal/permissions`;

export class PermissionsDefinitionRepository extends BaseRestRepository {
  private validResources?: Set<string>;

  public async getPermissionDefinitions(filter?: PermissionDefinitionFilter): Promise<PermissionDefinition[]> {
    const response = await this.fetchWithAuth(urlWithQuery(PERMISSIONS_URL, filter));
    return await this.getJsonResponse(response);
  }

  public async assertPermissionExists(permission: string): Promise<void> {
    if (permission === '*') {
      return;
    }
    const resource = permission.split(':')[0];
    const resources = await this.loadValidResources();
    if (resources && !resources.has(resource)) {
      throw new Error(`Cannot set permission "${permission}": "${resource}" is not a known backend permission resource.`);
    }
  }

  private async loadValidResources(): Promise<Set<string> | undefined> {
    if (this.validResources) {
      return this.validResources;
    }
    try {
      const definitions = await this.getPermissionDefinitions();
      this.validResources = new Set(definitions.map((definition) => definition.code.split(':')[0]));
      return this.validResources;
    } catch {
      return undefined;
    }
  }
}

export const permissionsDefinitionRepository = new PermissionsDefinitionRepository();
